/**
 * @fileoverview Save-capability-changes mutation hook.
 * @description Wraps the batch grant/revoke call in a TanStack mutation and refreshes the matrix
 * afterwards, whatever the outcome — and waits for that refetch before the mutation settles — so the
 * table always reflects what the server now holds.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'

import { CAPABILITIES_QUERY_KEY } from '@/features/capabilities/constants/capabilities-query-keys.constants'
import { saveCapabilityChanges } from '@/features/capabilities/services/role-capabilities.service'

import type {
  CapabilityChange,
  SaveCapabilityChangesResponse,
} from '@/features/capabilities/types/capabilities.types'
import type { ApiError } from '@/shared/types/http.types'

/**
 * @description Saves a batch of grants and revokes.
 * @returns {ReturnType<typeof useMutation<SaveCapabilityChangesResponse, ApiError,
 * CapabilityChange[]>>} The mutation object (mutate, isPending, error, ...).
 */
export function useSaveCapabilityChanges() {
  const queryClient = useQueryClient()

  return useMutation<SaveCapabilityChangesResponse, ApiError, CapabilityChange[]>({
    mutationFn: (changes) => saveCapabilityChanges(changes),
    // Returning the refetch keeps the mutation pending until the matrix is fresh, so the page
    // never flashes the old ticks between leaving edit mode and the new ones arriving.
    onSettled: () => queryClient.invalidateQueries({ queryKey: CAPABILITIES_QUERY_KEY }),
  })
}
